import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  type Omittable,
} from "@salestrak/ui";

type NameFormDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rejects to keep the dialog open — the caller has already said why. */
  onSubmit: (name: string) => Promise<void>;
  /** Present in edit mode; absent means create. */
  initialName?: string;
  title: string;
  description: string;
  /** The Name input's placeholder ("Source name"). */
  placeholder: string;
  /** The verb on the primary button ("Create", "Save"). */
  primaryLabel: string;
}>;

/**
 * Create or edit one word of a vocabulary whose rows carry a name and nothing
 * else — a source, a contact method. The Outcomes form is its own dialog
 * because an outcome also carries a tone; this one asks the name alone. The
 * copy is the caller's: the frame does not know which vocabulary it serves.
 */
export function NameFormDialog({
  open,
  onOpenChange,
  onSubmit,
  initialName,
  title,
  description,
  placeholder,
  primaryLabel,
}: NameFormDialogProps) {
  const [name, setName] = useState(initialName ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    if (open) {
      setName(initialName ?? "");
      setNameError("");
    }
  }, [open, initialName]);

  async function handleSubmit() {
    if (submitting) return;
    // A blank name says so, rather than a press that does nothing.
    if (!name.trim()) {
      setNameError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(name.trim());
      onOpenChange(false);
    } catch {
      // Refused (a taken name): the caller toasted the reason, and the typed
      // name stays for the person to fix.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        dialogOptions={{
          primary: { label: primaryLabel, onClick: handleSubmit, loading: submitting },
          cancel: { disabled: submitting },
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <Box
            as="form"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <Field invalid={!!nameError}>
              <FieldLabel>Name</FieldLabel>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError("");
                }}
                placeholder={placeholder}
                autoFocus
              />
              {nameError ? <FieldError match>{nameError}</FieldError> : null}
            </Field>
          </Box>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
